import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn, firstName } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useResource } from "@/hooks/use-resource";
import { Skeleton } from "@/components/common";
import { errMsg } from "@/lib/api";
import { LosMark } from "@/components/Logo";
import { Attention, Recent, SinceLast, briefing, digest } from "@/components/oversikt/Blocks";
import { StartComposer } from "@/components/intake/StartComposer";
import { isFixture } from "@/components/work/workModel";

const HERO = "/brand/scana-skarpenord.jpg";
// The yard, faint, bleeding off the top-right edge of the surface – it fades into the page long before the text.
const BACKDROP_MASK = "linear-gradient(to left, black 0%, black 28%, transparent 88%), linear-gradient(to bottom, black 0%, black 22%, transparent 100%)";

/** Quiet pill – a filter when `active` is defined, otherwise a shortcut. */
function Pill({ active, onClick, count, children, testId }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn("inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-[background-color,border-color,color] duration-150", active ? "border-transparent bg-black/[0.06] text-foreground" : "border-black/[0.08] bg-white text-foreground/70 hover:border-black/[0.16] hover:text-foreground")} data-testid={testId}>
      {children}{count ? <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">{count}</span> : null}
    </button>
  );
}

/**
 * Hjem – one start point, then what a good colleague would say. The mark, one question and the composer first; under it a
 * row of quiet pills, and the work that needs you as recommendations with one next step each. No dashboard, no noise.
 */
export default function OversiktPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: d, error } = useResource("/sk/oversikt", null, { interval: 60000 });
  const inbox = useResource("/inbox", null, { interval: 45000 });
  const [view, setView] = useState("attention");
  const [all, setAll] = useState(false);
  const attention = (d?.attention || []).filter((p) => !isFixture(p));
  const line = d ? briefing(attention) : null;
  const facts = d ? digest(attention) : [];
  const intro = line ? `${line}${facts.length ? ` ${facts.join(" · ")}.` : ""}` : null;
  const name = firstName(user?.name);
  return (
    <div className="relative w-full" data-testid="oversikt-page">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-2 hidden h-[420px] overflow-hidden lg:block" data-testid="oversikt-backdrop">
        <div className="absolute inset-y-0 right-0 w-[min(60%,1000px)] bg-cover bg-[center_40%] opacity-[0.36]" style={{ backgroundImage: `url(${HERO})`, maskImage: BACKDROP_MASK, WebkitMaskImage: BACKDROP_MASK, maskComposite: "intersect", WebkitMaskComposite: "source-in" }} />
      </div>
      <div className="relative mx-auto w-full max-w-[820px] px-5 pb-28 pt-12 sm:px-[30px] lg:pt-20">
        <header className="flex flex-col items-center text-center" data-testid="oversikt-greeting">
          <LosMark tone="bare" className="h-9 w-9" />
          <h1 className="mt-6 font-sans text-[30px] font-medium leading-[1.15] tracking-[-0.022em] text-foreground sm:text-[32px]" data-testid="oversikt-title">{name ? `Hva vil du jobbe med i dag, ${name}?` : "Hva vil du jobbe med i dag?"}</h1>
          <p className="mt-3 max-w-[520px] text-[15px] leading-[1.6] text-muted-foreground" data-testid="oversikt-subline">Last opp en forespørsel eller beskriv oppgaven. Assistenten leser først og oppretter ingenting før du sier fra.</p>
          <StartComposer className="mt-8 w-full max-w-[760px]" placeholder="Beskriv oppgaven, eller legg ved en forespørsel …" />
          <div className="mt-5 flex flex-wrap items-center justify-center gap-1.5" data-testid="oversikt-pills">
            <Pill active={view === "attention"} onClick={() => setView("attention")} count={attention.length || null} testId="oversikt-pill-attention">Trenger oppmerksomhet</Pill>
            <Pill active={view === "recent"} onClick={() => setView("recent")} testId="oversikt-pill-recent">Fortsett der du slapp</Pill>
            <Pill onClick={() => navigate("/innboks")} count={inbox.data?.unread || null} testId="oversikt-pill-inbox">Nye forespørsler</Pill>
            <Pill onClick={() => navigate("/arbeid")} testId="oversikt-pill-work">Åpne saker</Pill>
          </div>
        </header>
        {error ? <div className="mt-10 rounded-xl border border-[hsl(var(--status-bad)/0.3)] bg-[hsl(var(--status-bad)/0.06)] px-4 py-3 text-[14px]" data-testid="oversikt-error">{errMsg(error)}</div> : null}
        {!d ? <div className="mt-16 space-y-6"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-20" /><Skeleton className="h-20" /></div> : (
          <div className="stagger mt-16 space-y-14" data-testid="oversikt-body" data-view={view}>
            {view === "attention" ? (
              <>
                <Attention items={attention} open={all} onMore={() => setAll(true)} intro={intro} />
                <Recent rows={d.recent} />
              </>
            ) : (
              <>
                <Recent rows={d.recent} title="Fortsett der du slapp" limit={8} />
                <Attention items={attention} open={false} onMore={() => { setView("attention"); setAll(true); }} />
              </>
            )}
            <SinceLast events={d.since} threads={d.threads} />
          </div>
        )}
      </div>
    </div>
  );
}
